/**
 * The set of records a merge panel is offering, and the principal it proposes.
 *
 * Kept free of Vue so it stays testable with Vitest, like the rest of
 * `@c/addressBook`.
 */

/**
 * The record the merge should default to keeping, or null for an empty
 * selection.
 *
 * The most referenced record wins (spec §6.5 section 1): it is the one that
 * minimises what the reassignment has to move. Ties break on the id, in
 * ascending order, so that two openings of the panel — or two users looking
 * at the same duplicates — agree on the same default rather than seeing it
 * shift under them.
 *
 * A record's `reference_count` can be null when its source could not count
 * it, and null is not zero: a record that genuinely holds no reference must
 * still outrank one nobody could count. Comparing with `??` would erase that
 * distinction by treating both as zero, so the comparison below keeps null at
 * the bottom explicitly instead.
 *
 * An archived record is never a valid principal (spec 6.5.1) — excluded
 * before comparing, not just deprioritised, since the whole point of
 * "archived" is that it should not be the record everything else ends up
 * pointing at.
 *
 * @param {Array<{id: string, reference_count: number|null, is_archived?: boolean}>} records
 * @returns {string|null}
 */
export function suggestedPrincipalId(records) {
  const eligible = records.filter((record) => !record.is_archived)

  if (eligible.length === 0) {
    return null
  }

  return eligible.reduce((best, record) =>
    isBetterPrincipal(record, best) ? record : best
  ).id
}

/**
 * Whether `candidate` should be preferred over `current` as the principal.
 *
 * @param {{id: string, reference_count: number|null}} candidate
 * @param {{id: string, reference_count: number|null}} current
 * @returns {boolean}
 */
function isBetterPrincipal(candidate, current) {
  const candidateCount = candidate.reference_count
  const currentCount = current.reference_count

  if (candidateCount === currentCount) {
    return candidate.id < current.id
  }

  // An uncounted source loses to a counted one, zero included: null is
  // "unknown", not "least".
  if (candidateCount === null) {
    return false
  }

  if (currentCount === null) {
    return true
  }

  return candidateCount > currentCount
}

/**
 * Add a record to the selection, unless it is already in it.
 *
 * @param {Array<{id: string}>} records
 * @param {{id: string}} record
 * @returns {Array<{id: string}>}
 */
export function addRecord(records, record) {
  if (records.some((existing) => existing.id === record.id)) {
    return records
  }

  return [...records, record]
}

/**
 * Drop a record from the selection by id. A no-op for an unknown id.
 *
 * @param {Array<{id: string}>} records
 * @param {string} id
 * @returns {Array<{id: string}>}
 */
export function removeRecord(records, id) {
  return records.filter((record) => record.id !== id)
}

/**
 * Whether the current selection is still worth merging.
 *
 * @param {Array<unknown>} records
 * @returns {boolean}
 */
export function canMerge(records) {
  return records.length >= 2
}

/**
 * The ids of a duplicate group fetched through the listing, and the size of the
 * whole group.
 *
 * The total is `meta.total`, not the number of ids: the page is capped, so only
 * the total says whether the group fits in one merge (`duplicateGroupExceedsCap`).
 *
 * @param {{entries: Array<{id: string}>, meta?: {total?: number}}} listing
 * @returns {{ids: Array<string>, total: number}}
 */
export function duplicateGroupFromListing(listing) {
  const ids = listing.entries.map((entry) => entry.id)

  return { ids, total: listing.meta?.total ?? ids.length }
}

/**
 * The preview warning raised when two selected records are the deceased of
 * distinct dossiers (`MergePreviewWarning::SeveralDeceasedDossiers`).
 *
 * @type {string}
 */
export const SEVERAL_DECEASED_DOSSIERS_WARNING = 'several_deceased_dossiers'

/**
 * Whether a preview raises the several-deceased warning.
 *
 * @param {{warnings?: Array<string>}|null} preview
 * @returns {boolean}
 */
export function warnsOfSeveralDeceased(preview) {
  return (preview?.warnings ?? []).includes(SEVERAL_DECEASED_DOSSIERS_WARNING)
}

/**
 * The dossiers the several-deceased warning concerns, for the panel to link
 * to; empty when the preview does not raise it.
 *
 * @param {{warnings?: Array<string>, deceased_dossiers?: Array<{id: string, label: string}>}|null} preview
 * @returns {Array<{id: string, label: string}>}
 */
export function severalDeceasedDossiers(preview) {
  if (!warnsOfSeveralDeceased(preview)) {
    return []
  }

  return preview.deceased_dossiers ?? []
}
